import { AreaVars } from '@/integrations/crystallize/types/localization';
import { Bundle } from '@/ui/services/BundleService';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { urlParameterCodes } from '@/constants/urlParams';
import DeviceTypeEnum from '@/integrations/serviceAPI/internal-tools/enums/device-type-enum';
import Cookies from 'js-cookie';
import { INSTALLATION_CHECK_COOKIE_NAME } from '@/utils/constants';
import { v4 as uuid } from 'uuid';
import { paths } from '@/constants/paths';
import axios from 'axios';
import { apiPaths } from '@/constants/apiPaths';
import ProductSKUEnum from '@/enums/product-sku-enum';
import getRecommendedProductData from './server-utils';
import { MAX_INNER_CYLINDER_LENGTH_LIMIT, MAX_OUTER_CYLINDER_LENGTH_LIMIT } from './constants';
import {
  Compatibility,
  CylinderExchangePossibleAnswer,
  CylinderTypeAnswer,
  CylinderTypeNordicsAnswer,
  EmergencyFeatureAnswer,
  ICFormAnswers,
  ICQuestions,
  ICStepConfig,
  ICStepName,
  ICSurveyStep,
  KeyDimensionsAnswer,
  LockManufacturerAnswer,
  LockTypeAnswer,
  MoreCylindersUSAnswer,
} from './types';
import { RecommendedProductProps } from './ICRecommendedProduct/RecommendedProduct';

export function getEuResultStep(formData: ICFormAnswers): ICStepName {
  const cylinderType = formData?.icSurvey_cylinderType as CylinderTypeAnswer;
  const isNordics = [
    CylinderTypeNordicsAnswer.NORDIC_ROUND,
    CylinderTypeNordicsAnswer.SCANDIC_OVAL,
  ].includes(formData.icSurvey_cylinderTypeNordics as any);

  // Absolute max: inside > 82mm or outside > 85mm → restricted to Pro/Go
  const insideLength = Number(formData?.icSurvey_insideCylinderLength);
  const outsideLength = Number(formData?.icSurvey_outsideCylinderLength);
  if (
    insideLength > MAX_INNER_CYLINDER_LENGTH_LIMIT ||
    outsideLength > MAX_OUTER_CYLINDER_LENGTH_LIMIT
  ) {
    return ICStepName.RestrictedCompatibilityGoPro;
  }

  if (isNordics) {
    return ICStepName.RestrictedCompatibilityNordics;
  }

  // Lane for EU and Swiss is the same now
  if ([CylinderTypeAnswer.SWISS, CylinderTypeAnswer.EURO].includes(cylinderType)) {
    // Not supported knob and not willing to exchange cylinder
    if (
      formData.icSurvey_lockType === LockTypeAnswer.KNOB &&
      formData.icSurvey_lockManufacturer === LockManufacturerAnswer.OTHER
    ) {
      // Open to exchange cylinder
      if (formData.icSurvey_cylinderExchangePossible === CylinderExchangePossibleAnswer.YES) {
        return ICStepName.RestrictedUltraCompatibility;
      }

      // Not wanting to exchange cylinder
      if (formData.icSurvey_cylinderExchangePossible === CylinderExchangePossibleAnswer.NO) {
        return ICStepName.BadCompatibility;
      }
    }

    if (formData.icSurvey_lockType === LockTypeAnswer.KEY) {
      if (formData.icSurvey_emergencyFeature === EmergencyFeatureAnswer.NO) {
        if (formData.icSurvey_cylinderExchangePossible === CylinderExchangePossibleAnswer.NO) {
          return ICStepName.RestrictedCompatibilityGoPro;
        }
        if (formData.icSurvey_cylinderExchangePossible === CylinderExchangePossibleAnswer.YES) {
          return ICStepName.RestrictedUltraCompatibility;
        }
      }
    }
  }

  if (
    formData.icSurvey_cylinderType === CylinderTypeAnswer.UK_OVAL &&
    formData.icSurvey_lockType === LockTypeAnswer.KNOB
  ) {
    return ICStepName.RestrictedCompatibilityGoUK;
  }

  // Knob supported manufacturer
  if (
    formData.icSurvey_lockType === LockTypeAnswer.KNOB &&
    formData.icSurvey_lockManufacturer !== LockManufacturerAnswer.OTHER
  ) {
    return ICStepName.RestrictedCompatibilityUltraGo;
  }

  if (
    formData.icSurvey_lockType === LockTypeAnswer.KNOB &&
    formData.icSurvey_lockManufacturer === LockManufacturerAnswer.OTHER &&
    cylinderType === CylinderTypeAnswer.EURO
  ) {
    return ICStepName.RestrictedUltraCompatibility;
  }

  if (
    cylinderType === CylinderTypeAnswer.UK_OVAL &&
    formData.icSurvey_emergencyFeature === EmergencyFeatureAnswer.YES
  ) {
    return ICStepName.RestrictedCompatibilityGoUK;
  }

  if (formData.icSurvey_emergencyFeature === EmergencyFeatureAnswer.NO) {
    return ICStepName.RestrictedUltraCompatibility;
  }

  if (formData.icSurvey_keyDimensions === KeyDimensionsAnswer.NO) {
    return ICStepName.RestrictedUltraCompatibility;
  }

  if (formData.icSurvey_cylinderExchangePossible === CylinderExchangePossibleAnswer.NO) {
    return ICStepName.RestrictedCompatibilityGoPro;
  }

  return ICStepName.GoodCompatibility;
}

export function getUsResultStep(formData: ICFormAnswers): ICStepName {
  // No single cylinder → check what type they have
  if (formData.icSurveyUS_singleCylinder === 'no') {
    if (formData.icSurveyUS_moreCylinders === MoreCylindersUSAnswer.EURO) {
      return ICStepName.RestrictedUsEuro;
    }

    return ICStepName.BadCompatibility;
  }

  // Multiple cylinder deadbolts → bad compatibility
  if (formData.icSurveyUS_multipleCylinders === 'yes') {
    return ICStepName.BadCompatibility;
  }

  // Happy path: RestrictedUs (Nuki SL)
  return ICStepName.RestrictedUs;
}

export function getResultScreenCrystallizePath(stepName: ICStepName) {
  switch (stepName) {
    case ICStepName.RestrictedCompatibilityNordics:
      return paths.installationCheckResultsRestrictedNordics;
    case ICStepName.RestrictedCompatibilityUltraGo:
      return paths.installationCheckResultsRestrictedUltraGo;
    case ICStepName.RestrictedCompatibilityGoPro:
      return paths.installationCheckResultsRestrictedGoPro;
    case ICStepName.RestrictedUltraCompatibility:
      return paths.installationCheckResultsRestrictedUltra;
    case ICStepName.RestrictedCompatibilityGo:
      return paths.installationCheckResultsRestrictedGo;
    case ICStepName.RestrictedCompatibilityGoUK:
      return paths.installationCheckResultsRestrictedGoUK;
    case ICStepName.RestrictedUs:
      return paths.installationCheckResultsRestrictedUs;
    case ICStepName.RestrictedUsEuro:
      return paths.installationCheckResultsRestrictedUsEuro;
    default:
      return paths.installationCheckResultsGoodCompatibility;
  }
}

const SWISS_SKUS = new Set<string>([
  ProductSKUEnum.SMART_LOCK_5G_ULTRA_ROUND_CH,
  ProductSKUEnum.SMART_LOCK_5G_PRO_ROUND_CH,
  ProductSKUEnum.SMART_LOCK_5G_GO_ROUND_CH,
]);

// If user chooses a certain cylinder type, recommended product might depend on it.
export function getPreferredVariant(
  variants: Array<{ sku: string; images?: any; priceVariant?: any }> | null | undefined,
  cylinderType?: CylinderTypeAnswer,
) {
  if (!variants || variants.length === 0) {
    return undefined;
  }

  if (cylinderType === CylinderTypeAnswer.SWISS) {
    return variants.find((v) => SWISS_SKUS.has(v.sku)) || variants[0];
  }

  return variants[0];
}

export const fetchProductRecommendation = async (
  language: string,
  areaVariables?: AreaVars,
  productPaths?: string[],
  cylinderType?: CylinderTypeAnswer,
): Promise<Partial<RecommendedProductProps>[]> => {
  if (!areaVariables) {
    console.error('Missing area variables in localization');
    return [];
  }

  if (!productPaths || productPaths.length === 0) {
    return [];
  }

  try {
    const productFetchPromises = productPaths.map((path) =>
      getRecommendedProductData(
        language,
        path,
        areaVariables.priceVariantId,
        areaVariables.stockLocationId,
      ),
    );

    const productDataArray = await Promise.all(productFetchPromises);

    const items = productDataArray
      .filter(Boolean) // Filter out any null/undefined data
      .map((productData) => {
        if (!productData) {
          return null;
        }

        // Bundle
        if (productData.__typename === 'Document') {
          const bundleData = new Bundle(productData, areaVariables.priceVariantId);

          return {
            name: bundleData.name,
            description: bundleData.productNames,
            image: bundleData.firstImage,
            price: bundleData.finalPrice,
            discountedPrice:
              bundleData.totalDiscount > 0 ? bundleData.totalOriginalPrice : undefined,
            buttonText: 'Choose Variant',
            path: bundleData.getLinkPath() || '',
          };
        }
        if (productData.__typename === 'Product') {
          const variant = getPreferredVariant(productData.variants, cylinderType);
          const skuParam = variant?.sku
            ? `&${urlParameterCodes.productDetail.sku}=${variant.sku}`
            : '';

          // Single product
          return {
            name: productData.name || 'Missing name',
            image: variant?.images?.[0],
            price: variant?.priceVariant?.price || undefined,
            buttonText: 'Choose Variant',
            path:
              `${productData.path}?${urlParameterCodes.productDetail.chooserOpen}=true${skuParam}` ||
              '#',
            description: componentByTypename(
              productData.shortDescription?.content,
              'RichTextContent',
            ),
          };
        }
        return null;
      })
      .filter(Boolean);

    return items as Partial<RecommendedProductProps>[];
  } catch (error) {
    console.error('Error fetching product recommendation:', error);
    return [];
  }
};

export const getICIDFromCookie = (): { surveyId: string; newICSurvey: boolean } | undefined => {
  try {
    const existingICID = Cookies.get(INSTALLATION_CHECK_COOKIE_NAME);
    if (existingICID) {
      return { surveyId: existingICID, newICSurvey: false };
    }
    const newICID = `IC-${uuid()}`;
    Cookies.set(INSTALLATION_CHECK_COOKIE_NAME, newICID, { expires: 365 });
    return { surveyId: newICID, newICSurvey: true };
  } catch (e) {
    console.error('Error getting IC ID from cookie:', e);
    return undefined;
  }
};

// taken from existing IC
export const trackICEventWithGtm = (question: string, answer: any) => {
  window?.dataLayer?.push({
    [question]: answer,
  });
};

// taken from existing IC
export const syncICEventToDb = async (SurveyStepData: ICSurveyStep): Promise<void> => {
  try {
    await axios.post(apiPaths.findYourSolution.storeAnswer, SurveyStepData);
  } catch (e) {
    console.error('Error saving survey answer:', e);
  }
};

// track using tracking api (taken from existing IC)
export const trackEventToDb = async (question: string, answer: any) => {
  // Sync to database
  const ICmetaInfo = getICIDFromCookie();
  if (ICmetaInfo !== undefined) {
    syncICEventToDb({
      surveyId: ICmetaInfo.surveyId,
      newICSurvey: ICmetaInfo.newICSurvey,
      question,
      answer: String(answer),
    });
  }
};

// taken from existing IC
export const saveAndTrack = async (variableName: string, value: any) => {
  console.log('Tracking event:', variableName, value);

  // Track event with GTM if GTM is in place/configured properly
  // for proper GTM setup, make sure you've set a GTM_ID in the .env file
  trackICEventWithGtm(variableName, value);

  // track to api db
  trackEventToDb(variableName, value);
};

export const trackCompatibilityScreenUs = (stepConfig: ICStepConfig, formValues: ICFormAnswers) => {
  // bail early
  if (stepConfig.type !== 'compatibilityScreen') {
    return;
  }

  let compatibility: string | undefined;

  if (stepConfig.compatibility === Compatibility.GOOD) {
    compatibility = 'full';
  } else if (stepConfig.compatibility === Compatibility.BAD) {
    compatibility = 'bad';
  } else if (stepConfig.compatibility === Compatibility.RESTRICTED) {
    if (formValues.icSurveyUS_multipleCylinders === 'yes') {
      compatibility = 'restrictedMultipleLocks';
    } else if (stepConfig.stepName === ICStepName.RestrictedUs) {
      compatibility = 'restrictedUs';
    } else if (stepConfig.stepName === ICStepName.RestrictedUsEuro) {
      compatibility = 'restrictedUsEuro';
    }
  }

  if (compatibility) {
    saveAndTrack('icSurveyUS_compatibility', compatibility);
  }
};

export const trackCompatibilityScreen = (
  stepConfig: ICStepConfig,
  compatibleProductTypes: DeviceTypeEnum[],
) => {
  // bail early
  if (stepConfig.type !== 'compatibilityScreen') {
    return;
  }

  let compatibility: string | undefined;

  if (stepConfig.compatibility === Compatibility.GOOD) {
    compatibility = 'fullCompatibilty';
  } else if (stepConfig.compatibility === Compatibility.BAD) {
    compatibility = 'noCompatibility';
  } else if (stepConfig.compatibility === Compatibility.RESTRICTED) {
    // Ultra only screen
    if (stepConfig.mainText === ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_ULTRA) {
      compatibility = 'restrictedCompatibilityUltra';
    }

    if (compatibleProductTypes?.length === 1) {
      if (compatibleProductTypes.includes(DeviceTypeEnum.SMART_LOCK_5G_GO)) {
        compatibility = 'restrictedCompatibilityGo';
      } else if (compatibleProductTypes.includes(DeviceTypeEnum.SMART_LOCK_5G_PRO)) {
        compatibility = 'restrictedCompatibilityPro';
      } else if (compatibleProductTypes.includes(DeviceTypeEnum.SMART_LOCK_5G_ULTRA)) {
        compatibility = 'restrictedCompatibilityUltra';
      }
    } else if (compatibleProductTypes.length > 0) {
      if (
        compatibleProductTypes.includes(DeviceTypeEnum.SMART_LOCK_5G_GO) &&
        compatibleProductTypes.includes(DeviceTypeEnum.SMART_LOCK_5G_PRO)
      ) {
        compatibility = 'restrictedCompatibilityProGo';
      }
    }
  }

  if (compatibility) {
    saveAndTrack('icSurvey_Compatibility', compatibility);
  }
};
